/**
 * Protected Customer Identity — display rules for the ERP frontend.
 *
 * Only the CEO and the developer (admin accounts, or an explicit
 * `view_customer_contact` permission) see a customer's full name, e-mail,
 * phone number and street address. Everyone else sees:
 *   - name     "Luis Berisha"                    -> "L. Berisha"
 *   - address  "Frankenstrasse 18, 6003 Luzern"  -> "6003 Luzern"
 *   - e-mail / phone hidden
 *
 * This mirrors the server-side masking (map-management PII middleware), which
 * is the real enforcement; this helper keeps screens consistent when a value
 * reaches the browser from a source that is not masked server-side yet.
 */
import { resolveProcessCustomerAddress } from './processCustomerAddress';

export const canViewCustomerContact = ({ isAdmin, hasPermission } = {}) =>
  isAdmin === true || (typeof hasPermission === 'function' && (hasPermission('view_customer_contact') || hasPermission('admin_access')));

/**
 * Any authenticated user (driver, repair technician, tour manager, dispatcher,
 * or administrator testing/managing the flow) can request or reveal temporary
 * PII access when contact details are masked or hidden on the current screen.
 */
export const canRequestPiiAccess = () => true;

export const maskCustomerName = (fullName) => {
  if (!fullName || typeof fullName !== 'string') return fullName;
  const name = fullName.trim();
  if (!name) return name;
  let given;
  let surname;
  if (name.includes(',')) {
    const [last, first] = name.split(',', 2).map((p) => p.trim());
    given = first || last;
    surname = first ? last : '';
  } else {
    const tokens = name.split(/\s+/);
    [given] = tokens;
    surname = tokens.length > 1 ? tokens[tokens.length - 1] : '';
  }
  const initial = given ? `${given[0].toUpperCase()}.` : '';
  return surname ? `${initial} ${surname}` : initial;
};

const LOCALITY_RE = /\b(\d{4,5})\s+([^\d,;\n][^,;\n]*)/u;

export const maskAddressToLocality = (address, postalCode, city) => {
  const locality = [postalCode, city].map((p) => (p ? String(p).trim() : '')).filter(Boolean).join(' ');
  if (locality) return locality;
  if (!address) return '';
  const match = String(address).match(LOCALITY_RE);
  return match ? `${match[1]} ${match[2].trim()}` : '';
};

export const formatProcessCustomerLocality = (customer = {}) => {
  const address = resolveProcessCustomerAddress(customer);
  if (!address) return '';
  return maskAddressToLocality(address.street, address.postalCode, address.city);
};

/**
 * True when the customer can be e-mailed: either the address is visible, or the
 * backend masked it but flagged that one exists (the notification service then
 * resolves the real recipient from the repair id — see recipient_process_id).
 */
export const customerEmailAvailable = (customer) =>
  Boolean(customer && (customer.email || customer.email_available));

/** Name to put into raw HTML e-mails: the real name for CEO/dev, otherwise a
 * placeholder the notification service replaces with the real name. */
export const emailSafeCustomerName = (name, showContact) =>
  (showContact ? name : '{{CUSTOMER_NAME}}');

export const maskCustomerDetails = ({ customerName, customerEmail, customerPhone } = {}) => ({
  customerName: maskCustomerName(customerName),
  customerEmail: '',
  customerPhone: '',
  contactHidden: Boolean(customerEmail || customerPhone),
});

/**
 * Incoming calls require the dedicated grant, CEO/Developer tags, or the two
 * designated owner accounts. Generic admin/PII permissions are not call grants.
 */
const CALL_ALLOWED_EMAILS = new Set([
  'admin@consumerworld.ch',
  'admin@escooterlab.ch',
]);

export const canReceiveCalls = ({ email, roles = [], tags = [], permissions = [], login_channel, user_type } = {}) => {
  if (login_channel === 'customer' || user_type === 'customer') return false;
  if (email && CALL_ALLOWED_EMAILS.has(String(email).toLowerCase().trim())) return true;
  const names = [...(Array.isArray(roles) ? roles : []), ...(Array.isArray(tags) ? tags : [])]
    .map(value => String(typeof value === 'object' ? value?.name || '' : value).trim().toLowerCase());
  return names.some(name => name === 'ceo' || name === 'developer') ||
    (Array.isArray(permissions) && permissions.includes('receive_customer_calls'));
};

// Operational contact display; server responses enforce the same policy.
const normalizeTag = (tag) => String(typeof tag === 'object' ? (tag?.name || tag?.tag?.name || '') : (tag || ''))
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[_-]/g, ' ').trim();

const protectedTags = new Set([
  'employee', 'mitarbeiter', 'mitarbeitende',
  'driver', 'fahrer', 'chauffeur', 'administration', 'office administration',
  'buro administration', 'buro', 'office', 'mechanic', 'mechaniker', 'mechanics',
  'technician', 'techniker', 'repair technician',
]);

export const hasProtectedContacts = (user) => !user ||
  user.login_channel === 'customer' || user.user_type === 'customer' ||
  (user.login_channel !== 'staff' &&
    (Array.isArray(user.roles) ? user.roles : [user.roles]).some(role =>
      ['customer', 'kunde', 'client'].includes(normalizeTag(role)))) ||
  [user?.roles, user?.tags]
  .flatMap(value => Array.isArray(value) ? value : (value ? [value] : []))
  .some(tag => protectedTags.has(normalizeTag(tag)));

export const maskedPhone = (value) => {
  const text = String(value || '').trim();
  if (!text || /^\+41\d{4}\*{3} \*{4} \*{2}$/.test(text)) return text;
  let digits = text.replace(/\D/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2);
  if (digits.startsWith('0') && digits.length === 10) digits = `41${digits.slice(1)}`;
  return digits.startsWith('41') && digits.length === 11
    ? `+${digits.slice(0, 6)}*** **** **` : 'Nummer verborgen';
};
